import React, { useEffect, useState, useCallback } from 'react';
import { Users, Loader2, Sparkles, Rocket, Link2, Plus } from 'lucide-react';
import { sessionAPI } from '../../services/api';
import { assignDeviceViaN8n } from '../../services/n8n';
import { useRoomDeviceBinding } from '../../hooks/useRoomDeviceBinding';
import RoomBindingCard from '../../components/tabletop/RoomBindingCard';
import SeatDeviceSelect from '../../components/tabletop/SeatDeviceSelect';
import {
  tabletopPersonasAPI,
  tabletopExercisesAPI,
  tabletopRunAPI,
  TabletopExercise,
  TabletopPersona,
  TabletopDeliveryChannel,
  TabletopPersonaControl,
} from '../../services/tabletopService';

interface Props {
  projectId: string;
  exercise: TabletopExercise;
  onExerciseChange: (exercise: TabletopExercise) => void;
}

const CONTROL_OPTIONS: TabletopPersonaControl[] = ['human', 'ai_driven', 'referenced_only'];
const CHANNEL_OPTIONS: TabletopDeliveryChannel[] = ['device_voice', 'browser_tts', 'text'];

// Phase 3's cast configuration surface (plan section 8): per-seat inclusion,
// control mode, delivery channel, device binding and assignee. Device
// binding now follows the platform's existing device-allocation approach
// (see DevicesTab.tsx / device-n8n.ts) instead of a raw text field: pair a
// room to the linked session, then pair a device already mapped to that
// room to a persona's "role" (personaId). n8n's own device state is the
// source of truth for the pairing at dispatch time
// (tabletop-clock.service.ts's findAssignedDeviceId) — persona.deviceId is
// kept in sync here purely as a display cache.
const CastSetupPage: React.FC<Props> = ({ projectId, exercise, onExerciseChange }) => {
  const [personas, setPersonas] = useState<TabletopPersona[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [promotingId, setPromotingId] = useState<string | null>(null);
  const [sessionIdInput, setSessionIdInput] = useState(exercise.sessionId || '');
  const [linkingSession, setLinkingSession] = useState(false);
  const [compressionRatio, setCompressionRatio] = useState(60);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Room ↔ device allocation (mirrors DevicesTab.tsx's flow)
  const binding = useRoomDeviceBinding(exercise.sessionId);
  const [pairingPersonaId, setPairingPersonaId] = useState<string | null>(null);

  const load = useCallback(() => {
    tabletopPersonasAPI.list(projectId, exercise.exerciseId).then(r => setPersonas(r.data.personas)).finally(() => setLoading(false));
  }, [projectId, exercise.exerciseId]);

  useEffect(() => { load(); }, [load]);

  const pairDevice = async (persona: TabletopPersona, deviceId: string) => {
    if (!exercise.sessionId) return;
    setPairingPersonaId(persona.personaId);
    setError(null);
    try {
      if (deviceId) {
        await assignDeviceViaN8n(deviceId, persona.personaId, exercise.sessionId);
      }
      updatePersona(persona.personaId, { deviceId: deviceId || undefined });
      await savePersona({ ...persona, deviceId: deviceId || undefined });
      await binding.reload();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setPairingPersonaId(null);
    }
  };

  const updatePersona = (personaId: string, patch: Partial<TabletopPersona>) => {
    setPersonas(prev => prev.map(p => p.personaId === personaId ? { ...p, ...patch } : p));
  };

  const savePersona = async (persona: TabletopPersona) => {
    setSavingId(persona.personaId);
    try {
      const { exerciseId, personaId, ...body } = persona;
      await tabletopPersonasAPI.put(projectId, exercise.exerciseId, personaId, body);
    } finally {
      setSavingId(null);
    }
  };

  const promote = async (personaId: string) => {
    setPromotingId(personaId);
    try {
      const r = await tabletopPersonasAPI.promote(projectId, exercise.exerciseId, personaId);
      updatePersona(personaId, { avatarDefinitionId: r.data.persona.avatarDefinitionId });
    } finally {
      setPromotingId(null);
    }
  };

  const linkSession = async (sessionId: string) => {
    if (!sessionId.trim()) return;
    setLinkingSession(true);
    setError(null);
    try {
      const r = await tabletopExercisesAPI.update(projectId, exercise.exerciseId, { sessionId: sessionId.trim() });
      onExerciseChange(r.data.exercise);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLinkingSession(false);
    }
  };

  // Creates a fresh session and links it in one step — the manual
  // paste-an-existing-ID flow below is kept as a fallback for exercises that
  // should run inside a session that already exists for another reason, but
  // most organisers testing/running a tabletop have no session yet and
  // shouldn't have to go create one elsewhere first to get an ID to paste.
  const createAndLinkSession = async () => {
    setLinkingSession(true);
    setError(null);
    try {
      const { sessionId } = await sessionAPI.createSession({
        customerName: exercise.orgProfile.sector ? `Tabletop — ${exercise.orgProfile.sector}` : 'Tabletop Exercise',
        goals: [],
        projectId,
      });
      setSessionIdInput(sessionId);
      await linkSession(sessionId);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
      setLinkingSession(false);
    }
  };

  const startRun = async () => {
    setStarting(true);
    setError(null);
    try {
      const r = await tabletopRunAPI.start(projectId, exercise.exerciseId, compressionRatio);
      onExerciseChange(r.data.exercise);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-8">Loading cast…</div>;
  }

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[900px] mx-auto flex flex-col gap-6">

        <header className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <Users size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Cast Setup</h1>
            <p className="text-slate-500 text-sm font-medium">Configure who plays each seat before STARTEX.</p>
          </div>
        </header>

        {error && (
          <div className="px-5 py-4 rounded-xl bg-red-50 border border-red-200 text-sm font-medium text-red-700">{error}</div>
        )}

        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#e2e8f0]">
            <h2 className="text-sm font-bold text-navy">Seats ({personas.length})</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {personas.map(persona => (
              <div key={persona.personaId} className="px-6 py-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-navy">{persona.displayName}</p>
                    <p className="text-xs text-slate-400">{persona.organisation} · {persona.category.replace(/_/g, ' ')}</p>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <input
                      type="checkbox"
                      checked={persona.included !== false}
                      onChange={e => { updatePersona(persona.personaId, { included: e.target.checked }); }}
                      onBlur={() => savePersona(persona)}
                    />
                    In this run
                  </label>
                </div>

                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-[10px] font-bold text-primary tracking-widest uppercase">Control</label>
                    <select
                      value={persona.control}
                      onChange={e => { updatePersona(persona.personaId, { control: e.target.value as TabletopPersonaControl }); }}
                      onBlur={() => savePersona(persona)}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-navy"
                    >
                      {CONTROL_OPTIONS.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-primary tracking-widest uppercase">Delivery</label>
                    <select
                      value={persona.deliveryChannel}
                      onChange={e => { updatePersona(persona.personaId, { deliveryChannel: e.target.value as TabletopDeliveryChannel }); }}
                      onBlur={() => savePersona(persona)}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-navy"
                    >
                      {CHANNEL_OPTIONS.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-primary tracking-widest uppercase">Device</label>
                    <div className="mt-1">
                      <SeatDeviceSelect
                        personaId={persona.personaId}
                        value={persona.deviceId}
                        roomId={binding.roomId}
                        roomDevices={binding.roomDevices}
                        allDevices={binding.allDevices}
                        disabled={persona.deliveryChannel !== 'device_voice' || pairingPersonaId === persona.personaId}
                        onChange={deviceId => pairDevice(persona, deviceId)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-primary tracking-widest uppercase">Assignee (participant)</label>
                    <input
                      value={persona.assignedParticipantId || ''}
                      onChange={e => updatePersona(persona.personaId, { assignedParticipantId: e.target.value })}
                      onBlur={() => savePersona(persona)}
                      disabled={persona.control !== 'human'}
                      placeholder="participant id"
                      className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-navy disabled:bg-slate-50 disabled:text-slate-300"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-[11px] text-slate-400">
                    {persona.avatarDefinitionId ? 'Linked to avatar catalogue' : 'Ephemeral persona — not yet in the catalogue'}
                  </p>
                  <div className="flex items-center gap-2">
                    {savingId === persona.personaId && <Loader2 size={12} className="animate-spin text-slate-400" />}
                    {!persona.avatarDefinitionId && (
                      <button
                        onClick={() => promote(persona.personaId)}
                        disabled={promotingId === persona.personaId}
                        className="flex items-center gap-1.5 text-[11px] font-semibold text-primary hover:text-primary-dark disabled:opacity-50"
                      >
                        {promotingId === persona.personaId ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                        Promote to catalogue
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {personas.length === 0 && <p className="px-6 py-8 text-sm text-slate-400 text-center">No cast for this exercise.</p>}
          </div>
        </div>

        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-6 flex flex-col gap-4">
          <h2 className="text-sm font-bold text-navy flex items-center gap-2"><Link2 size={16} className="text-primary" /> Linked session</h2>
          {exercise.sessionId ? (
            <p className="text-sm text-slate-600">Linked to session <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{exercise.sessionId}</span>.</p>
          ) : (
            <div className="flex flex-col gap-3">
              <button
                onClick={createAndLinkSession}
                disabled={linkingSession}
                className="self-start flex items-center gap-2 rounded-xl h-10 px-5 bg-primary text-white text-sm font-bold hover:opacity-90 disabled:opacity-40 shadow-sm"
              >
                {linkingSession ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                Create a new session for this run
              </button>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400">or link an existing one:</span>
                <input
                  value={sessionIdInput}
                  onChange={e => setSessionIdInput(e.target.value)}
                  placeholder="Existing session ID"
                  className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-navy"
                />
                <button
                  onClick={() => linkSession(sessionIdInput)}
                  disabled={linkingSession || !sessionIdInput.trim()}
                  className="flex items-center gap-2 rounded-xl h-10 px-5 bg-white border border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-50 disabled:opacity-40 shadow-sm"
                >
                  {linkingSession ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}
                  Link
                </button>
              </div>
            </div>
          )}
        </div>

        {exercise.sessionId && (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-slate-400 px-1">Physical devices can only be paired to seats once the session is bound to the room they're mapped to — same as every other session type.</p>
            <RoomBindingCard binding={binding} />
          </div>
        )}

        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-6 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-navy">Start the run</h2>
            <p className="text-xs text-slate-400 mt-1">Compression ratio — how many sim minutes pass per real minute.</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              value={compressionRatio}
              onChange={e => setCompressionRatio(Number(e.target.value) || 1)}
              className="w-20 rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-navy text-center"
            />
            <button
              onClick={startRun}
              disabled={starting || !exercise.sessionId}
              className="flex items-center gap-2 rounded-xl h-10 px-5 bg-primary text-white text-sm font-bold hover:opacity-90 disabled:opacity-40 shadow-sm"
            >
              {starting ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
              STARTEX
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CastSetupPage;
